/**
 * The live scoreboard (§1.9) and the rules in words. Scores come from the public 1 Hz standings
 * (`view.shares`: land share + score per player — the very `PlayerState.score` the 20:00
 * standings rank), ranked exactly as the sim's end-of-match standings are: score, then player id.
 * The briefing and the RULES · CONTROLS block are built from BALANCE, so a balance change cannot
 * leave them saying something else. Pure (tested in test/score.test.ts).
 */
import { BALANCE, TICK_HZ, type PlayerId, type PlayerView } from '@sa/sim';
import { clock } from './alerts';
export { FINAL_COUNT_TICKS, FINAL_MINUTE_TICKS } from './alerts';
import { KEY } from '../input/actions';

export interface ScoreRow {
  playerId: PlayerId;
  score: number;
  areaShare: number;
}

/** Every nation, best first: score, then player id (the order of `MatchResult.standings`). */
export function scoreboard(view: Pick<PlayerView, 'shares'>): ScoreRow[] {
  return view.shares.map((s) => ({ playerId: s.playerId, score: s.score, areaShare: s.areaShare })).sort((a, b) => b.score - a.score || a.playerId - b.playerId);
}

/**
 * Where we stand: `#3 · 87` (rank among every nation, as on the results screen) — in teams our
 * team's rank and summed score (a team's score is its members' sum, §1.9). Null before the first
 * standings arrive.
 */
export function ourStanding(view: Pick<PlayerView, 'shares' | 'me' | 'mode' | 'players'>): { rank: number; score: number; of: number; team: boolean } | null {
  if (!view.shares.length) return null;
  if (view.mode === 'teams') {
    const teamOf = (p: PlayerId) => view.players.find((q) => q.id === p)?.team ?? 0;
    const sums = new Map<number, number>();
    for (const s of view.shares) sums.set(teamOf(s.playerId), (sums.get(teamOf(s.playerId)) ?? 0) + s.score);
    const order = [...sums].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
    const mine = teamOf(view.me);
    const k = order.findIndex(([t]) => t === mine);
    return k < 0 ? null : { rank: k + 1, score: order[k][1], of: order.length, team: true };
  }
  const board = scoreboard(view);
  const k = board.findIndex((r) => r.playerId === view.me);
  return k < 0 ? null : { rank: k + 1, score: board[k].score, of: board.length, team: false };
}

/** `#3 · 87`, `TEAM #1 · 412` */
export function standingText(s: { rank: number; score: number; team: boolean }): string {
  return `${s.team ? 'TEAM ' : ''}#${s.rank} · ${s.score}`;
}

// ---------------------------------------------------------------------------------- the rules

const P = BALANCE.phases;
const V = BALANCE.victory;
const pct = (x: number) => `${Math.round(x * 100)}%`;
/** a duration (ticks) as m:ss */
const dur = (ticks: number) => clock(ticks / TICK_HZ);

/**
 * The goal in three lines (loading screen and the first frame of a match). Every m:ss is a
 * countdown, like the HUD clock: the match ends when it runs out, the phases come `IN` so long
 * from the start.
 */
export function briefing(matchTicks: number = BALANCE.matchTicks): string[] {
  const crit = BALANCE.capital.criticalTicks;
  return [
    `WIN: HIGHEST SCORE WHEN THE CLOCK RUNS OUT (LAND + POP + KILLS) · OR HOLD ${pct(V.dominationShare)} OF THE LAND FOR ${dur(V.dominationTicks)}`,
    `WEAPONS RELEASE IN ${dur(P.releaseTick)} · CAPITALS EXPOSED IN ${dur(P.capitalsExposedTick)}${matchTicks !== BALANCE.matchTicks ? ` · MATCH ${dur(matchTicks)}` : ''}`,
    crit > 0 ? `THEN: CAPITAL DESTROYED → CRITICAL FOR ${dur(crit)} · DESTROYED AGAIN → YOU ARE OUT` : 'THEN: CAPITAL DESTROYED → YOU ARE OUT',
  ];
}

/** A rules section for the Esc menu's RULES · CONTROLS: a heading and its [term, what] rows. */
export interface RulesSection {
  title: string;
  rows: [string, string][];
}

/** `4:00 IN · CLOCK 16:00`: a phase as time from the start and as the HUD clock will read it. */
const at = (tick: number, matchTicks: number) => `${dur(tick)} IN · CLOCK ${dur(matchTicks - tick)}`;

/** The rules (goal, timeline) and the controls, from BALANCE and the hotkey grid. */
export function rulesSections(matchTicks: number = BALANCE.matchTicks): RulesSection[] {
  const kw = BALANCE.scoring.killWeight;
  const crit = BALANCE.capital.criticalTicks;
  const K = (k: string) => k.toUpperCase();
  return [
    {
      title: 'GOAL',
      rows: [
        ['WIN', `HIGHEST SCORE WHEN THE ${dur(matchTicks)} CLOCK RUNS OUT`],
        ['SCORE', `0–1,000: ${pct(1 - kw)} LAND + POPULATION (HALF EACH) · ${pct(kw)} KILLS`],
        ['', 'LAND / POPULATION: YOUR SHARE OF ALL · KILLS: YOUR SHARE OF THE POPULATION KILLED ON RIVAL LAND'],
        ['DOMINATION', `HOLD ${pct(V.dominationShare)} OF ALL LAND FOR ${dur(V.dominationTicks)}: YOU WIN AT ONCE`],
        ['LAST STANDING', 'THE LAST NATION (OR TEAM) IN WINS · ALLIES DO NOT SHARE A WIN · A TEAM SCORES ITS SUM'],
      ],
    },
    {
      title: 'TIMELINE',
      rows: [
        ['WEAPONS RELEASE', `${at(P.releaseTick, matchTicks)} · SILOS, SUBS AND BOMBERS MAY STRIKE`],
        ['CAPITALS EXPOSED', `${at(P.capitalsExposedTick, matchTicks)} · LOSING YOUR CAPITAL CAN KNOCK YOU OUT`],
        ...(crit > 0
          ? ([
              ['', `CAPITAL DESTROYED OR CAPTURED → CRITICAL (L1) FOR ${dur(crit)}, BRACE READY · AGAIN → YOU ARE OUT`],
              ['', `NOT HIT AGAIN FOR ${dur(crit)} → IT RECOVERS`],
            ] as [string, string][])
          : []),
        ['LEADER BEACON', `${at(P.beaconTick, matchTicks)} · THE TOP SCORE'S CITIES SHOWN TO ALL, EVERY ${dur(P.beaconIntervalTicks)}`],
        ['FINAL', `${at(P.finalTick, matchTicks)} · BORDERS PUSH HARDER`],
      ],
    },
    {
      title: 'CONTROLS',
      rows: [
        ['RMB', 'PUSH YOUR BORDER THERE'],
        ['HOLD RMB', 'NATION MENU: CEASEFIRE · ALLIANCE · CREDITS · MARK · STRIKE'],
        ['1–7', 'BUILD RADAR · SAM · SILO · CITY · AIRBASE · SUB · CARRIER'],
        ['CLICK', 'SELECT A UNIT FOR ITS ORDERS'],
        [K(KEY.attack), 'ATTACK: LAUNCH, STRIKE (AGAIN: RIPPLE)'],
        [K(KEY.defend), 'DEFEND: SAM TARGET, PATROL'],
        [K(KEY.special), 'SCOUT · BRACE A CITY · SAM SILENT (RADAR OFF)'],
        [K(KEY.buy), 'BUY: MISSILE, INTERCEPTOR, FIGHTER, CITY UPGRADE'],
        [K(KEY.buy2), 'BUY A BOMBER'],
        [K(KEY.toggle), 'AUTO-SCRAMBLE ON / OFF'],
        [K(KEY.cancel), 'CANCEL · CLEAR · RECALL · STOP RIPPLE'],
        ['', `NOTHING SELECTED: ${K(KEY.attack)} STRIKES A CRITICAL CAPITAL · ${K(KEY.defend)} FOCUSES SAMS · ${K(KEY.special)} BRACES`],
        ['WASD', 'MOVE THE CAMERA (ARROWS TOO) · WHEEL ZOOMS'],
        ['SPACE', 'JUMP TO THE NEXT THREAT · NONE: HOME'],
        ['TAB', 'HOLD FOR SCORES'],
        ['Y / N', 'ACCEPT / DECLINE AN OFFER'],
        ['ESC', 'MENU · M SOUND'],
      ],
    },
  ];
}
